function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import c from 'classnames';
import { useField, useFormikContext } from 'formik';
import GenericFormElement from '../GenericFormElement';
import useAriaDescribedByField from '../../../hooks/useAriaDescribedByField';
const NumberField = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    name,
    className,
    label,
    placeholder,
    min,
    max,
    allowOutOfBounds,
    accuracy = 0,
    unit,
    unitAfter,
    required,
    disabled,
    defaultValue,
    verticalSpacing,
    visualValidation,
    onBlur,
    onChange,
    description,
    optionalLabel,
    allowDecimals,
    isLiveValidationDisabled
  } = props;
  const [field, meta, helpers] = useField(name);
  const {
    submitCount
  } = useFormikContext();
  const {
    ariaDescribedBy
  } = useAriaDescribedByField({
    field,
    meta,
    description
  });

  /**
   * Keep track of the form's value internally to prevent unexpected visual changes for the user.
   * We show the internal value in the field, while the value in the Formik state might be different.
   * On blur we reset the internal value to the Formik state value.
   */
  const [internalValue, setInternalValue] = React.useState(field.value);
  const [editing, setEditing] = React.useState(false);

  /**
   * Update the internal value when the field value changes.
   * This is necessary when the field value changes from the outside, e.g. when using the RangeWithInputField component.
   */
  React.useEffect(() => {
    if (!editing) {
      setInternalValue(field.value);
    }
  }, [field.value]);
  const classNames = c('NumberField', className);
  const containerClassNames = c('input-container', 'input-with-unit', {
    'input-with-unit--before': !unitAfter,
    'input-with-unit--after': unitAfter
  });
  return /*#__PURE__*/React.createElement(GenericFormElement, {
    name: name,
    label: label,
    meta: meta,
    helpers: helpers,
    className: classNames,
    visualValidation: visualValidation,
    defaultValue: defaultValue,
    containerClassName: containerClassNames,
    verticalSpacing: verticalSpacing,
    description: description,
    optionalLabel: optionalLabel,
    submitCount: submitCount,
    isLiveValidationDisabled: isLiveValidationDisabled
  }, /*#__PURE__*/React.createElement(React.Fragment, null, unit && !unitAfter && /*#__PURE__*/React.createElement("div", {
    className: "unit"
  }, unit), /*#__PURE__*/React.createElement("input", _extends({
    ref: ref,
    id: name,
    type: "text",
    spellCheck: false,
    inputMode: allowDecimals ? 'decimal' : 'numeric',
    placeholder: placeholder,
    min: min,
    max: max,
    step: 1 / 10 ** accuracy // ** is instead of Math.pow, as it is deprecated.
    ,
    required: required,
    disabled: disabled
  }, props, field, {
    value: String(internalValue),
    "aria-describedby": ariaDescribedBy,
    onFocus: () => {
      setEditing(true);
    },
    onBlur: e => {
      setEditing(false);
      if (onBlur) onBlur(e);
      field.onBlur(e);
      setInternalValue(field.value);
    },
    onKeyDown: e => {
      const {
        key
      } = e;
      const separator = getDecimalSeparatorForLocale();
      if (!allowDecimals && key === separator) {
        e.preventDefault();
      }
    },
    onChange: e => {
      const rawInput = e.target.value;
      // Allow both comma and dot as decimal separators by normalizing to dot.
      const normalizedInput = rawInput.replace(',', '.');
      const parsedValue = Number(normalizedInput);
      if (Number.isNaN(parsedValue)) {
        /**
         * We emulate the behaviour for input type="number" by preventing
         * the default handler if the parsed value cannot be converted to a
         * valid floating-point number.
         * @see https://html.spec.whatwg.org/multipage/input.html#number-state-(type%3Dnumber)
         */
        e.preventDefault();
        return;
      }

      // Show what the user typed, even if it's with a comma.
      setInternalValue(e.target.value);
      if (onChange) onChange(e);
      const isValidNumber = !Number.isNaN(parsedValue);
      if (!isValidNumber) {
        return;
      }

      /** Set the field value to the max value when the filled in value exceeds it. */
      if (max !== undefined && parsedValue > max && !allowOutOfBounds) {
        helpers.setValue(Number(max).toFixed(accuracy));
        return;
      }

      /** Set the field value to the min value when the filled in value falls below it. */
      if (min !== undefined && parsedValue < min && !allowOutOfBounds) {
        helpers.setValue(Number(min).toFixed(accuracy));
        return;
      }
      helpers.setValue(parsedValue.toFixed(accuracy));
    }
  })), unit && unitAfter && /*#__PURE__*/React.createElement("div", {
    className: "unit"
  }, unit)));
});
function getDecimalSeparatorForLocale() {
  const numberWithDecimalSeparator = 1.1;
  return numberWithDecimalSeparator.toLocaleString(navigator.language).substring(1, 2);
}
export default NumberField;